import { BrowserRouter, Route, Routes } from "react-router";
import Dashboard from "./pages/Dashboard";
import MainLayout from "./layout/MainLayout";
import UserIndex from "./pages/MasterData/User/Index";
import UserForm from "./pages/MasterData/User/Form";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { AuthProvider } from "./hooks/useAuth";
import { ProtectedRoute } from "./components/ProtectedRoute";
import LoginPage from "./pages/Login";
import RegistrationDetail from "./pages/Registration/Detail";
import { id } from "date-fns/locale";
import { setDefaultOptions } from "date-fns";
import ContactIndex from "./pages/contact/Index";
import ContactForm from "./pages/contact/Form";
import DisturbanceInformationIndex from "./pages/DisturbanceInformation/Index";
import DisturbanceInformationForm from "./pages/DisturbanceInformation/Form";
import FaqIndex from "./pages/CustomerCare/Faq/Index";
import FaqForm from "./pages/CustomerCare/Faq/Form";
import BannerIndex from "./pages/Banner/Index";
import BannerForm from "./pages/Banner/Form";
import RatePerCommodityIndex from "./pages/LabService/RatePerCommodity/Index";
import RatePerCommodityForm from "./pages/LabService/RatePerCommodity/Form";
import RatePerParemeterIndex from "./pages/LabService/RatePerParameter/Index";
import OtherRateIndex from "./pages/LabService/OtherRates/Index";
import BusinessAreaForm from "./pages/MasterData/BusinessArea/Form";
import BusinessAreaIndex from "./pages/MasterData/BusinessArea/Index";
import PendingRegistration from "./pages/Registration/Pending";
import ProcessRegistration from "./pages/Registration/Process";
import ProcessRegistrationDetail from "./pages/Registration/ProcessDetail";
import SurveyIndex from "./pages/Survey/Index";
import SurveyDetail from "./pages/Survey/Detail";
import DocumentAnalysisOfficerDetail from "./pages/DocumentAnalysis/OfficerDetail";
import DocumentAnalysisPending from "./pages/DocumentAnalysis/Pending";
import PendingDocumentAnalysisDetail from "./pages/DocumentAnalysis/Detail";
import CanceledDocumentAnalysis from "./pages/DocumentAnalysis/Canceled";
import CanceledDocumentAnalysisDetail from "./pages/DocumentAnalysis/CanceledDetail";
import ProcessDocumentAnalysis from "./pages/DocumentAnalysis/Process";
import ProcessDocumentAnalysisDetail from "./pages/DocumentAnalysis/ProcessDetail";
import RatePerParameterForm from "./pages/LabService/RatePerParameter/Form";
import OtherRateForm from "./pages/LabService/OtherRates/Form";
import ServiceOfficeIndex from "./pages/CustomerCare/ServiceOffice/Index";
import ServiceOfficeForm from "./pages/CustomerCare/ServiceOffice/Form";
import SurveyForm from "./pages/Survey/Form";
import Profile from "./pages/Profile";
import CustomerCategory from "./pages/BillSimulation/CustomerCategory";
import MaintenanceCost from "./pages/BillSimulation/MaintenanceCost";
import FixedExpenseCost from "./pages/BillSimulation/FixedExpenseCost";
import CubicationCostIndex from "./pages/BillSimulation/CubicationCost/Index";
import CubicationCostForm from "./pages/BillSimulation/CubicationCost/Form";
import HistoryLogIndex from "./pages/HistoryLog/Index";
import HistoryLogForm from "./pages/HistoryLog/Form";
import FaqCategoryIndex from "./pages/CustomerCare/FaqCategory/Index";
import RCCIndex from "./pages/Registration/RCC/Index";
import CanceledRegistrationIndex from "./pages/Registration/Canceled/Index";
import CanceledRegistrationDetail from "./pages/Registration/Canceled/Detail";
import FinishRegistrationIndex from "./pages/Registration/Finish/Index";
import FinishRegistrationDetail from "./pages/Registration/Finish/Detail";
import RCCDetail from "./pages/Registration/RCC/Detail";
import RccPaymentProofIndex from "./pages/Registration/RCC/PaymentProof/Index";
import RccPaymentProofDetail from "./pages/Registration/RCC/PaymentProof/Detail";
import TestimonyLaboratoryIndex from "./pages/LabService/Testimony/Index";
import TestimonyLaboratoryForm from "./pages/LabService/Testimony/Form";
import MasterDataTypeIndex from "./pages/MasterData/MasterDataType/Index";
import MasterDataItemIndex from "./pages/MasterData/MasterDataItem/Index";
import MasterDataTypeForm from "./pages/MasterData/MasterDataType/Form";
import RewardIndex from "./pages/MasterData/Reward/Index";
import UserManagementIndex from "./pages/UserManagement/UserAccount/Index";
import UserManagementDetail from "./pages/UserManagement/UserAccount/Detail";
import ComplaintCustomer from "./pages/Complaint/Customer";
import ComplaintDetail from "./pages/Complaint/Detail";
import ComplaintCategory from "./pages/Complaint/Category";
import ComplaintNonCustomer from "./pages/Complaint/NonCustomer";
import RegistrationAll from "./pages/Registration/All";
import StandMeterIndex from "./pages/StandMeter/Index";
import StandMeterDetail from "./pages/StandMeter/Detail";
import WaterTankIndex from "./pages/WaterTank/Index";
import WaterTankDetail from "./pages/WaterTank/Detail";
import CustomerVerificationIndex from "./pages/UserManagement/UserVerification/Index";
import RedeemRewardIndex from "./pages/UserManagement/RedeemReward/Index";
import CustomerVerificationDetail from "./pages/UserManagement/UserVerification/Detail";
// import ClearSurveyorLocalStorageOnRouteChange from "./components/ClearSurveyorLocalStorage"
setDefaultOptions({ locale: id });

const queryClient = new QueryClient();

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        {/* <ClearSurveyorLocalStorageOnRouteChange/> */}
        <AuthProvider>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <MainLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Dashboard />} />
              <Route path="history-log">
                <Route index element={<HistoryLogIndex />} />
                <Route path="detail/:id" element={<HistoryLogForm />} />
              </Route>
              <Route path="profile" element={<Profile />} />
              <Route path="bill-simulation">
                <Route
                  path="customer-category"
                  element={<CustomerCategory />}
                />
                <Route path="maintenance-cost" element={<MaintenanceCost />} />
                <Route path="fixed-cost" element={<FixedExpenseCost />} />
                <Route path="cubication-cost">
                  <Route index element={<CubicationCostIndex />} />
                  <Route path="create" element={<CubicationCostForm />} />
                  <Route path="detail/:id" element={<CubicationCostForm />} />
                </Route>
              </Route>
              <Route path="disturbance-information">
                <Route index element={<DisturbanceInformationIndex />} />
                <Route path="create" element={<DisturbanceInformationForm />} />
                <Route
                  path="detail/:id"
                  element={<DisturbanceInformationForm />}
                />
              </Route>
              <Route path="contact">
                <Route index element={<ContactIndex />} />
                <Route path="detail/:id" element={<ContactForm />} />
              </Route>
              <Route path="banner">
                <Route index element={<BannerIndex />} />
                <Route path="create" element={<BannerForm />} />
                <Route path="detail/:id" element={<BannerForm />} />
              </Route>
              <Route path="customer-care">
                <Route path="faq-category">
                  <Route index element={<FaqCategoryIndex />} />
                </Route>
                <Route path="faq">
                  <Route index element={<FaqIndex />} />
                  <Route path="create" element={<FaqForm />} />
                  <Route path="detail/:id" element={<FaqForm />} />
                </Route>
                <Route path="office">
                  <Route index element={<ServiceOfficeIndex />} />
                  <Route path="create" element={<ServiceOfficeForm />} />
                  <Route path="detail/:id" element={<ServiceOfficeForm />} />
                </Route>
              </Route>
              <Route path="lab-service">
                <Route path="rate-per-commodity">
                  <Route index element={<RatePerCommodityIndex />} />
                  <Route path="create" element={<RatePerCommodityForm />} />
                  <Route path="detail/:id" element={<RatePerCommodityForm />} />
                </Route>
                <Route path="rate-per-parameter">
                  <Route index element={<RatePerParemeterIndex />} />
                  <Route path="detail/:id" element={<RatePerParameterForm />} />
                </Route>
                <Route path="other-rates">
                  <Route index element={<OtherRateIndex />} />
                  <Route path="detail/:id" element={<OtherRateForm />} />
                </Route>
                <Route path="testimony">
                  <Route index element={<TestimonyLaboratoryIndex />} />
                  <Route path="create" element={<TestimonyLaboratoryForm />} />
                  <Route
                    path="detail/:id"
                    element={<TestimonyLaboratoryForm />}
                  />
                </Route>
              </Route>
              <Route path="master-data">
                <Route path="user">
                  <Route index element={<UserIndex />} />
                  <Route path="create" element={<UserForm />} />
                  <Route path="detail/:id" element={<UserForm />} />
                </Route>
                <Route path="master-data-type">
                  <Route index element={<MasterDataTypeIndex />} />
                  <Route path="detail/:id" element={<MasterDataTypeForm />} />
                </Route>
                <Route path="master-data-item">
                  <Route index element={<MasterDataItemIndex />} />
                </Route>
                <Route path="business-area">
                  <Route index element={<BusinessAreaIndex />} />
                  <Route path="create" element={<BusinessAreaForm />} />
                  <Route path="detail/:id" element={<BusinessAreaForm />} />
                </Route>
                <Route path="reward">
                  <Route index element={<RewardIndex />} />
                </Route>
              </Route>
              <Route path="data-selection">
                <Route path="data-selection-type">
                  <Route index element={<MasterDataTypeIndex />} />
                  <Route path="detail/:id" element={<MasterDataTypeForm />} />
                </Route>
                <Route path="data-selection-item">
                  <Route index element={<MasterDataItemIndex />} />
                </Route>
              </Route>
              <Route
                path="registration-pending"
                element={<PendingRegistration />}
              />
              <Route path="data-rcc">
                <Route index element={<RCCIndex />} />
                <Route path="detail/:id" element={<RCCDetail />} />
              </Route>
              <Route path="rcc/payment-proof">
                <Route index element={<RccPaymentProofIndex />} />
                <Route path="detail/:id" element={<RccPaymentProofDetail />} />
              </Route>
              <Route path="registration">
                <Route path="detail/:id" element={<RegistrationDetail />} />
                <Route path="process">
                  <Route index element={<ProcessRegistration />} />
                  <Route
                    path="detail/:id"
                    element={<ProcessRegistrationDetail />}
                  />
                </Route>
                <Route path="canceled">
                  <Route index element={<CanceledRegistrationIndex />} />
                  <Route
                    path="detail/:id"
                    element={<CanceledRegistrationDetail />}
                  />
                </Route>
              </Route>
              <Route path="registration-all">
                <Route index element={<RegistrationAll />} />
              </Route>
              <Route path="registration-finish">
                <Route index element={<FinishRegistrationIndex />} />
                <Route
                  path="detail/:id"
                  element={<FinishRegistrationDetail />}
                />
              </Route>
              <Route path="survey">
                <Route index element={<SurveyIndex />} />
                <Route path="detail/:id" element={<SurveyDetail />} />
                <Route path="create" element={<SurveyForm />} />
              </Route>
              <Route path="water-tank">
                <Route index element={<WaterTankIndex />} />
                <Route path="detail/:id" element={<WaterTankDetail />} />
              </Route>
              <Route path="user-management">
                <Route path="user-account">
                  <Route index element={<UserManagementIndex />} />
                  <Route path="detail/:id" element={<UserManagementDetail />} />
                </Route>
                <Route path="customer-verification">
                  <Route index element={<CustomerVerificationIndex />} />
                  <Route path="detail/:id" element={<CustomerVerificationDetail />} />
                </Route>
                <Route path="redeem" element={<RedeemRewardIndex />} />
              </Route>
              <Route path="stand-meter">
                <Route index element={<StandMeterIndex />} />
                <Route path="detail/:id" element={<StandMeterDetail />} />
              </Route>
              <Route path="document-analysis">
                <Route path="pending">
                  <Route index element={<DocumentAnalysisPending />} />
                  <Route
                    path="detail/:id"
                    element={<PendingDocumentAnalysisDetail />}
                  />
                </Route>
                <Route path="process">
                  <Route index element={<ProcessDocumentAnalysis />} />
                  <Route
                    path="detail/:id"
                    element={<ProcessDocumentAnalysisDetail />}
                  />
                </Route>
                <Route path="canceled">
                  <Route index element={<CanceledDocumentAnalysis />} />
                  <Route
                    path="detail/:id"
                    element={<CanceledDocumentAnalysisDetail />}
                  />
                </Route>
                <Route
                  path="detail/:id/officer"
                  element={<DocumentAnalysisOfficerDetail />}
                />
              </Route>
              <Route path="complaint">
                <Route path="non-customer" element={<ComplaintNonCustomer />} />
                <Route path="customer" element={<ComplaintCustomer />} />
                <Route path="detail/:id" element={<ComplaintDetail />} />
                <Route path="category" element={<ComplaintCategory />} />
              </Route>
            </Route>
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}

export default App;
